import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { Capacidade } from '@via-tax/contracts'
import { useState, type ReactNode } from 'react'
import type { ContextoSessao } from '../tipos'
import { useTRPC } from '../trpc'
import iconeVia from '../assets/via-icone.png'
import { SeletorEmpresa } from './SeletorEmpresa'

export type Tela = 'dashboard' | 'empresa' | 'habilitar' | 'integracoes' | 'enviar' | 'documentos' | 'qualidade' | 'produtos' | 'insumos' | 'fornecedores' | 'clientes' | 'credito' | 'pendencias' | 'foco2027' | 'simples' | 'panorama' | 'relatorios' | 'regimes' | 'calculadora' | 'receitas'

interface ItemMenu {
  rotulo: string
  tela?: Tela
  /** fase do roadmap em que a tela chega — item aparece, mas desligado */
  fase?: string
  exige?: Capacidade
  exigeEmpresa?: boolean
  soStaff?: boolean
}

/** Navegação da arquitetura §12.1. O que ainda não existe mostra a fase em que chega. */
const GRUPOS: Array<{ titulo?: string; itens: ItemMenu[] }> = [
  { itens: [{ rotulo: 'Dashboard', tela: 'dashboard' }] },
  {
    titulo: 'Documentos',
    itens: [
      { rotulo: 'Enviar XML', tela: 'enviar', exigeEmpresa: true, exige: 'upload_xml' },
      { rotulo: 'Documentos recebidos', tela: 'documentos', exigeEmpresa: true },
      { rotulo: 'Qualidade da base', tela: 'qualidade', exigeEmpresa: true, soStaff: true },
    ],
  },
  {
    titulo: 'Revisão de Estoque',
    itens: [
      { rotulo: 'Produtos de saída', tela: 'produtos', exigeEmpresa: true, soStaff: true },
      { rotulo: 'Insumos de entrada', tela: 'insumos', exigeEmpresa: true, soStaff: true },
    ],
  },
  {
    titulo: 'Contrapartes',
    itens: [
      { rotulo: 'Fornecedores (regime)', tela: 'fornecedores', exigeEmpresa: true, exige: 'ver_contrapartes' },
      { rotulo: 'Clientes (mix de destinação)', tela: 'clientes', exigeEmpresa: true, exige: 'ver_contrapartes' },
      { rotulo: 'Crédito em risco', tela: 'credito', exigeEmpresa: true, exige: 'ver_contrapartes' },
    ],
  },
  {
    titulo: 'Simulador Tributário',
    itens: [
      { rotulo: 'Panorama 2027 → 2033', tela: 'panorama', exigeEmpresa: true },
      { rotulo: 'Foco 2027', tela: 'foco2027', exigeEmpresa: true },
      { rotulo: 'Simples: puro × híbrido', tela: 'simples', exigeEmpresa: true },
      { rotulo: 'Simples × Presumido × Real', tela: 'regimes', exigeEmpresa: true },
      { rotulo: 'Calculadora de preço', tela: 'calculadora', exigeEmpresa: true },
    ],
  },
  { itens: [{ rotulo: 'Pendências', tela: 'pendencias', exigeEmpresa: true }, { rotulo: 'Relatórios', tela: 'relatorios', exigeEmpresa: true }] },
  {
    titulo: 'Configurações',
    itens: [
      { rotulo: 'Dados da empresa', tela: 'empresa', exigeEmpresa: true },
      { rotulo: 'Receitas sem nota', tela: 'receitas', exigeEmpresa: true },
      { rotulo: 'Habilitar empresas', tela: 'habilitar', exige: 'habilitar_empresa', soStaff: true },
      { rotulo: 'Integrações', tela: 'integracoes', exige: 'ver_integracoes', soStaff: true },
    ],
  },
]

function alternarTema() {
  const raiz = document.documentElement
  const atual = raiz.dataset.theme ?? (matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
  const novo = atual === 'dark' ? 'light' : 'dark'
  raiz.dataset.theme = novo
  try {
    localStorage.setItem('via-tax:tema', novo)
  } catch {
    /* navegador sem storage: o tema só não persiste */
  }
}

export function Shell({ contexto, tela, ir, children }: { contexto: ContextoSessao; tela: Tela; ir: (t: Tela) => void; children: ReactNode }) {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const [menuAberto, setMenuAberto] = useState(false)
  const sair = useMutation(trpc.sessao.logout.mutationOptions({ onSuccess: () => qc.clear() }))
  const staff = contexto.tipoPrincipal === 'STAFF'

  const visivel = (i: ItemMenu) => {
    if (i.soStaff && !staff) return false
    // Item de empresa vale a capacidade na empresa ativa (sem empresa, a do tenant); item do escritório, a do tenant.
    const caps = (i.exigeEmpresa && contexto.empresaAtiva ? contexto.capacidades : contexto.capacidadesTenant) as string[]
    if (i.exige && !caps.includes(i.exige)) return false
    return true
  }

  return (
    <div className="flex h-full">
      <aside
        className={`fixed inset-y-0 left-0 z-30 w-64 shrink-0 overflow-y-auto bg-lateral text-lateral-tinta transition-transform md:static md:translate-x-0 ${menuAberto ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className="flex h-14 items-center gap-2 px-5">
          <img src={iconeVia} alt="Via Contabilidade" className="size-8 rounded bg-white p-0.5" />
          <span className="leading-tight">
            <span className="block text-[15px] font-semibold tracking-tight text-white">Via Tax</span>
            <span className="block text-[10px] text-lateral-tinta/70">Via Contabilidade</span>
          </span>
          <span className="num ml-auto text-[10px] text-lateral-tinta/60">F4</span>
        </div>
        <nav className="flex flex-col gap-4 px-3 pb-6 pt-2" aria-label="Navegação principal">
          {GRUPOS.map((g, gi) => {
            const itens = g.itens.filter(visivel)
            if (!itens.length) return null
            return (
              <div key={gi}>
                {g.titulo && (
                  <p className="mb-1 px-2 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-lateral-tinta/55">{g.titulo}</p>
                )}
                <ul className="flex flex-col gap-0.5">
                  {itens.map((i) => {
                    const desligado = !i.tela || (i.exigeEmpresa && !contexto.empresaAtiva)
                    const ativo = i.tela === tela
                    return (
                      <li key={i.rotulo}>
                        <button
                          type="button"
                          disabled={desligado}
                          onClick={() => {
                            if (i.tela) ir(i.tela)
                            setMenuAberto(false)
                          }}
                          className={`flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-[13px] transition focus-visible:outline-2 focus-visible:outline-acao ${
                            ativo
                              ? 'bg-white/10 font-medium text-white'
                              : desligado
                                ? 'cursor-default text-lateral-tinta/45'
                                : 'hover:bg-white/5 hover:text-white'
                          }`}
                          aria-current={ativo ? 'page' : undefined}
                          title={i.fase ? `Chega na fase ${i.fase} do roadmap` : undefined}
                        >
                          <span>{i.rotulo}</span>
                          {i.fase && <span className="num text-[10px] text-lateral-tinta/40">{i.fase}</span>}
                        </button>
                      </li>
                    )
                  })}
                </ul>
              </div>
            )
          })}
        </nav>
      </aside>
      {menuAberto && <button className="fixed inset-0 z-20 bg-black/30 md:hidden" aria-label="Fechar menu" onClick={() => setMenuAberto(false)} />}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 shrink-0 items-center gap-3 border-b border-linha bg-painel px-4 md:px-6">
          <button className="rounded p-1.5 text-tinta-2 hover:bg-fundo md:hidden" onClick={() => setMenuAberto(true)} aria-label="Abrir menu">
            ☰
          </button>
          <SeletorEmpresa contexto={contexto} />
          <div className="ml-auto flex items-center gap-1">
            <button
              onClick={alternarTema}
              className="rounded px-2 py-1.5 text-xs text-tinta-2 hover:bg-fundo hover:text-tinta"
              title="Alternar tema claro/escuro"
            >
              Tema
            </button>
            <div className="hidden text-right leading-tight sm:block">
              <p className="text-[13px] font-medium">{contexto.usuario.nome}</p>
              <p className="text-[11px] text-tinta-3">{rotuloPapel(contexto.papel ?? contexto.papelTenant)}</p>
            </div>
            <button
              onClick={() => sair.mutate()}
              className="ml-2 rounded px-2 py-1.5 text-xs text-tinta-2 hover:bg-fundo hover:text-tinta"
            >
              Sair
            </button>
          </div>
        </header>
        <main className="min-w-0 flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-5xl px-4 py-6 md:px-8 md:py-8">{children}</div>
        </main>
      </div>
    </div>
  )
}

const PAPEIS: Record<string, string> = {
  staff_admin: 'Administrador do escritório',
  staff_fiscal: 'Fiscal do escritório',
  staff_leitura: 'Leitura (escritório)',
  cliente_admin: 'Administrador da empresa',
  cliente_leitura: 'Leitura (empresa)',
}
const rotuloPapel = (p: string | null) => (p ? (PAPEIS[p] ?? p) : 'Sem empresa selecionada')
